import { useState, useEffect } from 'react';
import {
  Link2, Loader2, AlertCircle, CheckCircle2, RefreshCw, Unlink,
  Mail, Sparkles, Upload, ExternalLink, Shield, ArrowDown, ArrowUp, Info
} from 'lucide-react';

const CONSENT_TEXT_VERSION = '2026-05-12.1';

interface MbtiInfo { code: string; name: string; short: string; }
interface Persona { adjective: string | null; role: string | null; full: string | null; }

interface KpStatus {
  connected: boolean;
  status?: 'pending' | 'connected' | 'reverify_required' | 'disconnected' | 'error';
  kp_email?: string;
  consent_granted_at?: string;
  consent_text_version?: string;
  last_synced_at?: string;
  last_pushed_at?: string;
  auto_push_enabled?: boolean;
  auto_pull_enabled?: boolean;
  personality?: MbtiInfo | null;
  interest_profile?: string | null;
  persona?: Persona | null;
  career_interests?: Record<string, number> | null;
  interests?: string[] | null;
  last_error?: { code: string; message: string } | null;
}

const RIASEC_LABELS_NB: Record<string, string> = {
  realistic: 'Realistisk', investigative: 'Undersøkende', artistic: 'Kunstnerisk',
  social: 'Sosial', enterprising: 'Foretaksom', conventional: 'Konvensjonell',
};

function fmtDate(s?: string | null): string {
  if (!s) return '';
  try { return new Date(s).toLocaleString('nb-NO', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return s; }
}

export function KarrierepassetSection() {
  const [status, setStatus] = useState<KpStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  // Connect flow
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showOtpStep, setShowOtpStep] = useState(false);
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Disconnect modal
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [deleteCachedData, setDeleteCachedData] = useState(true);
  const [deleteCareerImports, setDeleteCareerImports] = useState(false);

  // Pre-push preview modal
  const [showPushPreview, setShowPushPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  async function fetchStatus() {
    try {
      const res = await fetch('/api/candidate/karrierepasset/status', { credentials: 'include' });
      if (res.status === 503) { setStatus(null); setError('Karrierepasset-integrasjon er ikke aktivert ennå'); return; }
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Kunne ikke hente status'); return; }
      setStatus(data); setError(null);
    } catch (e: any) { setError(e.message || 'Nettverksfeil'); }
    finally { setLoading(false); }
  }

  useEffect(() => { fetchStatus(); }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('kp_connected') === '1') {
      setActionMsg('Karrierepasset koblet til!');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (p.get('kp_error')) {
      setError('Karrierepasset-feil: ' + p.get('kp_error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  async function handleRequestOtp() {
    setActionLoading(true); setError(null); setActionMsg(null);
    try {
      const res = await fetch('/api/candidate/karrierepasset/connect/start', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Kunne ikke be om kode'); return; }
      if (!data.exists) {
        setError('Ingen Karrierepasset-konto funnet for denne e-posten. Opprett konto på karrierepasset.no først, eller bruk Vipps-innloggingen vi tilbyr.');
        return;
      }
      setShowOtpStep(true);
      setActionMsg(`Kode sendt til ${email}. Sjekk innboksen din (avsender: noreply@mail.synchrocity.no, fra-navn "Kompetanseattest").`);
    } catch (e: any) { setError(e.message || 'Nettverksfeil'); }
    finally { setActionLoading(false); }
  }

  async function handleVerifyOtp() {
    setActionLoading(true); setError(null);
    try {
      const consent_payload = {
        we_pull_displayed: ['e-post', 'fornavn', 'etternavn', 'personlighetstype (MBTI)', 'karriereinteresser (RIASEC/Holland)', 'persona', 'beskrivelse', 'interesser'],
        we_pull_stored_for_optional_import: ['arbeidserfaring', 'utdanning', 'ferdigheter', 'språk', 'kurs', 'sertifiseringer', 'referanser', 'jobbpreferanser'],
        we_push: ['kompetanseattester (som utdanning)', 'jobberfaring fra karriereprofil', 'VP-vitnemål'],
        auto_push_default: true,
        auto_pull_default: true,
        disconnect_consequence: 'Cached personlighet/persona slettes fra vår side. Data du har sendt til Karrierepasset må du selv slette på karrierepasset.no.',
        timestamp: new Date().toISOString(),
      };
      const res = await fetch('/api/candidate/karrierepasset/connect/verify', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: otpCode.trim(), consent_text_version: CONSENT_TEXT_VERSION, consent_payload }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Verifisering feilet'); return; }
      setActionMsg('Karrierepasset koblet til!');
      setShowConsentModal(false); setShowOtpStep(false); setOtpCode(''); setEmail('');
      await fetchStatus();
    } catch (e: any) { setError(e.message || 'Nettverksfeil'); }
    finally { setActionLoading(false); }
  }

  async function handleRefresh() {
    setActionLoading(true); setError(null);
    try {
      const res = await fetch('/api/candidate/karrierepasset/refresh', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'reverify_required') setError('Du må verifisere på nytt. Klikk "Koble til på nytt" nedenfor.');
        else setError(data.error || 'Refresh feilet');
        return;
      }
      setActionMsg('Oppdatert fra Karrierepasset'); await fetchStatus();
    } catch (e: any) { setError(e.message || 'Nettverksfeil'); }
    finally { setActionLoading(false); }
  }

  async function handleTogglePref(field: 'auto_push_enabled' | 'auto_pull_enabled', value: boolean) {
    setActionLoading(true); setError(null);
    try {
      const res = await fetch('/api/candidate/karrierepasset/preferences', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Kunne ikke oppdatere innstilling'); return; }
      await fetchStatus();
    } catch (e: any) { setError(e.message || 'Nettverksfeil'); }
    finally { setActionLoading(false); }
  }

  async function handleOpenPushPreview() {
    setLoadingPreview(true); setError(null);
    try {
      const res = await fetch('/api/candidate/karrierepasset/push-preview', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'reverify_required') {
          setError('Du må verifisere på nytt før push.');
        } else {
          setError(data.error || 'Kunne ikke hente forhåndsvisning');
        }
        return;
      }
      setPreviewData(data);
      setShowPushPreview(true);
    } catch (e: any) { setError(e.message || 'Nettverksfeil'); }
    finally { setLoadingPreview(false); }
  }

  async function handleConfirmPush() {
    setActionLoading(true); setError(null);
    try {
      const res = await fetch('/api/candidate/karrierepasset/push', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ include_certificates: true }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Push feilet'); return; }
      const eduAdded = data.education?.added || 0;
      const weAdded = data.work_experience?.added || 0;
      const eduSkipped = data.education?.skipped || 0;
      const weSkipped = data.work_experience?.skipped || 0;
      setActionMsg(`Sendt: ${eduAdded} attester (${eduSkipped} duplikater hoppet over), ${weAdded} jobberfaring (${weSkipped} duplikater)`);
      setShowPushPreview(false);
      setPreviewData(null);
      await fetchStatus();
    } catch (e: any) { setError(e.message || 'Nettverksfeil'); }
    finally { setActionLoading(false); }
  }

  async function handleDisconnect() {
    setActionLoading(true); setError(null);
    try {
      const res = await fetch('/api/candidate/karrierepasset/link', {
        method: 'DELETE', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          delete_imported_data: deleteCachedData,
          delete_career_history_imports: deleteCareerImports,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Frakobling feilet'); return; }
      setActionMsg(
        `Karrierepasset koblet fra. ${data.cached_data_deleted ? 'Personlighet/persona slettet. ' : 'Personlighet/persona beholdt. '}` +
        (data.career_history_imports_deleted > 0 ? `${data.career_history_imports_deleted} importerte jobberfaringer slettet.` : '')
      );
      setShowDisconnectModal(false);
      await fetchStatus();
    } catch (e: any) { setError(e.message || 'Nettverksfeil'); }
    finally { setActionLoading(false); }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 flex items-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
        <span className="text-gray-600">Laster Karrierepasset-status...</span>
      </div>
    );
  }

  if (status === null && error?.includes('ikke aktivert')) return null;

  return (
    <>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b bg-gradient-to-r from-kf-blue-50 to-indigo-50">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Link2 className="w-5 h-5 text-kf-blue-600" />
                Karrierepasset
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Koble til Karrierepasset for å berike CV-en og kursanbefalingene dine med personlighet og interesser.
              </p>
            </div>
            {status?.connected && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                <CheckCircle2 className="w-3 h-3" /> Koblet til
              </span>
            )}
            {status?.status === 'reverify_required' && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-kf-orange-100 text-kf-orange-800 text-xs font-medium">
                <AlertCircle className="w-3 h-3" /> Re-verifisering nødvendig
              </span>
            )}
          </div>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          {actionMsg && !error && (
            <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{actionMsg}</span>
            </div>
          )}

          {/* NOT CONNECTED */}
          {!status?.connected && status?.status !== 'reverify_required' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">Med Karrierepasset-koblingen får du:</p>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside ml-2">
                <li>Personlighetstype og karriereinteresser i CV-en din</li>
                <li>Bedre kursanbefalinger tilpasset din profil</li>
                <li>Automatisk synkronisering av kompetanseattester og jobberfaring til Karrierepasset</li>
              </ul>
              <button
                onClick={() => { setShowConsentModal(true); setShowOtpStep(false); setEmail(''); setOtpCode(''); }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700 transition text-sm font-medium"
              >
                <Link2 className="w-4 h-4" />
                Koble til Karrierepasset
              </button>
            </div>
          )}

          {/* RE-VERIFY REQUIRED */}
          {status?.status === 'reverify_required' && (
            <div className="space-y-3 border-l-4 border-kf-orange-400 pl-4">
              <p className="text-sm text-gray-700">
                Tilliten til Karrierepasset er utløpt. Vennligst koble til på nytt.
              </p>
              <button
                onClick={() => { setShowConsentModal(true); setShowOtpStep(false); setEmail(status.kp_email || ''); }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-kf-orange-600 text-white rounded-lg hover:bg-kf-orange-700"
              >
                <Mail className="w-4 h-4" /> Verifiser på nytt
              </button>
            </div>
          )}

          {/* CONNECTED */}
          {status?.connected && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500 text-xs uppercase tracking-wide">E-post hos KP</div>
                  <div className="font-mono text-gray-900 break-all">{status.kp_email}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs uppercase tracking-wide">Sist synkronisert</div>
                  <div className="text-gray-900">{fmtDate(status.last_synced_at)}</div>
                </div>
              </div>

              {/* Persona */}
              {status.persona?.full && (
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-5">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-wide text-purple-600 font-medium">Din profil</div>
                      <div className="text-2xl font-bold text-gray-900 mt-1">{status.persona.full}</div>
                      <div className="text-sm text-gray-600 mt-2 flex flex-wrap gap-3">
                        {status.personality && (
                          <span><strong className="text-purple-700">{status.personality.code}</strong> <span className="text-gray-500">— {status.personality.name}</span></span>
                        )}
                        {status.interest_profile && (
                          <span><strong className="text-kf-blue-700">{status.interest_profile}</strong> <span className="text-gray-500">(Holland)</span></span>
                        )}
                      </div>
                      {status.personality?.short && <p className="text-sm text-gray-700 mt-2 italic">{status.personality.short}</p>}
                    </div>
                  </div>
                </div>
              )}

              {!status.persona?.full && status.personality && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-wide text-purple-600 font-medium">Personlighetstype</div>
                      <div className="text-xl font-bold text-gray-900 mt-1">
                        {status.personality.code} <span className="font-normal text-gray-700">— {status.personality.name}</span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{status.personality.short}</p>
                      <p className="text-xs text-gray-500 mt-2">💡 Ta interesse-testen på Karrierepasset for å låse opp din komplette karriereprofil.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* RIASEC scores when available */}
              {status.career_interests && Object.keys(status.career_interests).length > 0 && (
                <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4">
                  <div className="text-xs uppercase tracking-wide text-kf-blue-600 font-medium mb-2">Karriereinteresse-score</div>
                  <div className="space-y-1.5">
                    {Object.entries(status.career_interests).sort((a, b) => b[1] - a[1]).map(([dim, val]) => (
                      <div key={dim} className="flex items-center gap-2">
                        <div className="w-32 text-sm text-gray-700 capitalize">{RIASEC_LABELS_NB[dim] || dim}</div>
                        <div className="flex-1 bg-white rounded-full h-2 overflow-hidden">
                          <div className="bg-kf-blue-500 h-full" style={{ width: `${Math.min(100, Math.max(0, val))}%` }} />
                        </div>
                        <div className="text-xs text-gray-600 w-10 text-right">{val.toFixed(0)}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Auto-sync settings */}
              <div className="border-t pt-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">Synkronisering</h3>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={status.auto_push_enabled !== false}
                    onChange={e => handleTogglePref('auto_push_enabled', e.target.checked)}
                    disabled={actionLoading}
                    className="mt-0.5"
                  />
                  <div className="text-sm">
                    <div className="font-medium text-gray-900 flex items-center gap-1">
                      <ArrowUp className="w-3.5 h-3.5 text-green-600" /> Auto-push til Karrierepasset
                    </div>
                    <div className="text-gray-500 text-xs">
                      Nye kompetanseattester sendes automatisk til Karrierepasset som utdanning.
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={status.auto_pull_enabled !== false}
                    onChange={e => handleTogglePref('auto_pull_enabled', e.target.checked)}
                    disabled={actionLoading}
                    className="mt-0.5"
                  />
                  <div className="text-sm">
                    <div className="font-medium text-gray-900 flex items-center gap-1">
                      <ArrowDown className="w-3.5 h-3.5 text-kf-blue-600" /> Auto-pull fra Karrierepasset
                    </div>
                    <div className="text-gray-500 text-xs">
                      Personlighet, persona og karriereinteresser oppdateres automatisk fra Karrierepasset.
                    </div>
                  </div>
                </label>

                {/* Manual buttons only when auto is off */}
                <div className="flex flex-wrap gap-2 pt-2">
                  {status.auto_pull_enabled === false && (
                    <button
                      onClick={handleRefresh}
                      disabled={actionLoading}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-kf-blue-100 text-kf-blue-700 rounded text-sm hover:bg-kf-blue-200 disabled:opacity-50"
                    >
                      {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Hent oppdatert data manuelt
                    </button>
                  )}
                  {status.auto_push_enabled === false && (
                    <button
                      onClick={handleOpenPushPreview}
                      disabled={actionLoading || loadingPreview}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200 disabled:opacity-50"
                    >
                      {loadingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      Send dine attester manuelt
                    </button>
                  )}
                </div>
                {status.last_pushed_at && (
                  <p className="text-xs text-gray-500">Sist pushet: {fmtDate(status.last_pushed_at)}</p>
                )}
              </div>

              {/* Footer actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <a href="https://karrierepasset.no" target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200">
                  <ExternalLink className="w-4 h-4" />
                  Gå til karrierepasset.no
                </a>
                <button
                  onClick={() => setShowDisconnectModal(true)}
                  disabled={actionLoading}
                  className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 text-red-700 hover:bg-red-50 rounded text-sm"
                >
                  <Unlink className="w-4 h-4" />
                  Koble fra
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* =================== CONSENT MODAL =================== */}
      {showConsentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b bg-gradient-to-r from-kf-blue-50 to-purple-50 sticky top-0">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-kf-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Samtykke til datadeling med Karrierepasset</h3>
                  <p className="text-sm text-gray-600 mt-1">Vennligst les og godta før kobling etableres.</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5 text-sm">
              {!showOtpStep ? (
                <>
                  <section>
                    <h4 className="font-semibold text-gray-900 flex items-center gap-1 mb-2">
                      <ArrowDown className="w-4 h-4 text-kf-blue-600" /> Vi henter fra Karrierepasset (vises automatisk):
                    </h4>
                    <ul className="list-disc list-inside ml-2 text-gray-700 space-y-0.5">
                      <li>E-postadresse, fornavn, etternavn</li>
                      <li>Personlighetstype (MBTI 4-tegns kode, eks. ENTP)</li>
                      <li>Karriereinteresser (Holland/RIASEC 3-tegns kode, eks. SEC)</li>
                      <li>Persona (lokalisert rolle-navn, eks. "Analytisk Fornyer")</li>
                      <li>Beskrivelse av personlighet (fritekst)</li>
                      <li>Interesser (selv-rapportert)</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className="font-semibold text-gray-900 flex items-center gap-1 mb-2">
                      <ArrowDown className="w-4 h-4 text-kf-blue-500 opacity-70" /> Vi mottar også (ikke vist automatisk — kan importeres ved aktivt valg):
                    </h4>
                    <ul className="list-disc list-inside ml-2 text-gray-700 space-y-0.5 text-xs">
                      <li>Arbeidserfaring (kan kopieres til din karriereprofil hos oss)</li>
                      <li>Utdanning fra din Karrierepasset-profil</li>
                      <li>Ferdigheter, språk, kurs, sertifiseringer, referanser, jobbpreferanser</li>
                    </ul>
                    <p className="text-xs text-gray-500 mt-1 italic">
                      Disse lagres trygt og vises ikke uten at du selv velger å importere dem.
                    </p>
                  </section>

                  <section>
                    <h4 className="font-semibold text-gray-900 flex items-center gap-1 mb-2">
                      <ArrowUp className="w-4 h-4 text-green-600" /> Vi sender til Karrierepasset:
                    </h4>
                    <ul className="list-disc list-inside ml-2 text-gray-700 space-y-0.5">
                      <li>Kompetanseattester du har fått hos oss (sendes som utdanning)</li>
                      <li>Jobberfaring fra din karriereprofil (om du har lagt inn noe)</li>
                      <li>VP-vitnemål fra Vitnemålsportalen (om du har importert)</li>
                    </ul>
                  </section>

                  <section className="bg-kf-blue-50 border border-kf-blue-200 rounded p-3">
                    <h4 className="font-semibold text-kf-blue-900 flex items-center gap-1 mb-1">
                      <Info className="w-4 h-4" /> Automatisk synkronisering
                    </h4>
                    <p className="text-kf-blue-800 text-xs">
                      Når du kobler til, vil nye attester sendes automatisk til Karrierepasset, og personlighetsdata
                      hentes automatisk daglig. Du kan slå dette av når som helst i innstillingene.
                    </p>
                  </section>

                  <section className="bg-amber-50 border border-amber-200 rounded p-3">
                    <h4 className="font-semibold text-amber-900 flex items-center gap-1 mb-1">
                      <Info className="w-4 h-4" /> Frakobling og sletting
                    </h4>
                    <p className="text-amber-800 text-xs">
                      Du kan koble fra Karrierepasset når som helst. Da fjerner vi personlighet og persona fra vår side.
                      Data du har sendt til Karrierepasset må du selv slette på{' '}
                      <a href="https://karrierepasset.no" target="_blank" rel="noreferrer" className="underline">karrierepasset.no</a>.
                    </p>
                  </section>

                  <section>
                    <h4 className="font-semibold text-gray-900 mb-2">Steg 1: E-post for verifisering</h4>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="din.epost@example.no"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-kf-blue-500 focus:border-kf-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Bruk samme e-post som du har på Karrierepasset.
                    </p>
                  </section>

                  <div className="flex gap-2 pt-4 border-t">
                    <button
                      onClick={handleRequestOtp}
                      disabled={actionLoading || !email.trim()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-kf-blue-600 text-white rounded-lg disabled:opacity-50"
                    >
                      {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                      Aksepter og send kode
                    </button>
                    <button
                      onClick={() => setShowConsentModal(false)}
                      className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                    >Avbryt</button>
                  </div>
                </>
              ) : (
                <>
                  <section>
                    <h4 className="font-semibold text-gray-900 mb-2">Steg 2: Skriv inn 6-sifret kode</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Kode er sendt til <strong>{email}</strong> fra noreply@mail.synchrocity.no.
                      Avsendernavn er "Kompetanseattest". Koden utløper om 10 minutter.
                    </p>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={otpCode}
                      onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456"
                      className="w-full px-3 py-3 border border-gray-300 rounded-md font-mono text-center text-xl tracking-widest"
                      autoFocus
                    />
                  </section>
                  <div className="flex gap-2 pt-4 border-t">
                    <button
                      onClick={handleVerifyOtp}
                      disabled={actionLoading || otpCode.length !== 6}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-kf-blue-600 text-white rounded-lg disabled:opacity-50"
                    >
                      {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                      Verifiser og koble til
                    </button>
                    <button
                      onClick={() => setShowOtpStep(false)}
                      className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                    >Tilbake</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* =================== PUSH PREVIEW MODAL =================== */}
      {showPushPreview && previewData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b bg-kf-blue-50 sticky top-0">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Upload className="w-5 h-5 text-kf-blue-600" />
                Forhåndsvis push til Karrierepasset
              </h3>
              <p className="text-sm text-gray-600 mt-1">Sjekk hva som ligger der fra før, og hva som vil bli sendt.</p>
            </div>

            <div className="p-6 space-y-4 text-sm">
              {/* Eksisterende hos KP */}
              <section>
                <h4 className="font-semibold text-gray-900 mb-2">Allerede på Karrierepasset:</h4>
                <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-2">
                  <div>
                    <div className="text-xs font-medium text-gray-700">Utdanning ({previewData.summary.kp_education_count})</div>
                    {previewData.summary.kp_education_from_us > 0 && (
                      <div className="text-xs text-gray-500">
                        {previewData.summary.kp_education_from_us} fra Kompetanseattest (våre tidligere push)
                      </div>
                    )}
                    {previewData.summary.kp_education_from_other_sources > 0 && (
                      <div className="text-xs text-gray-500">
                        {previewData.summary.kp_education_from_other_sources} fra andre kilder (din egen registrering)
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-700">Arbeidserfaring ({previewData.summary.kp_work_experience_count})</div>
                    {previewData.summary.kp_work_experience_from_us > 0 && (
                      <div className="text-xs text-gray-500">
                        {previewData.summary.kp_work_experience_from_us} fra Kompetanseattest
                      </div>
                    )}
                    {previewData.summary.kp_work_experience_from_other_sources > 0 && (
                      <div className="text-xs text-gray-500">
                        {previewData.summary.kp_work_experience_from_other_sources} fra andre kilder
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* Vi vil sende */}
              <section>
                <h4 className="font-semibold text-gray-900 mb-2">Vi sender (alt nedenfor; duplikater hoppes over):</h4>
                <div className="bg-kf-blue-50 border border-kf-blue-200 rounded p-3 space-y-2">
                  <div>
                    <div className="text-xs font-medium text-kf-blue-900">
                      Attester: {previewData.summary.our_certs_total} totalt — {previewData.summary.our_certs_already_pushed} allerede pushet
                    </div>
                    {previewData.our_data.certificates.length > 0 && (
                      <ul className="text-xs text-gray-700 mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                        {previewData.our_data.certificates.map((c: any) => (
                          <li key={c.id} className="flex items-center gap-1">
                            {c.already_pushed ? <span className="text-gray-400">✓</span> : <span className="text-kf-blue-600">+</span>}
                            <span className={c.already_pushed ? 'text-gray-500' : ''}>{c.title}</span>
                            {c.ects > 0 && <span className="text-gray-500">({c.ects} ECTS)</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <div className="text-xs font-medium text-kf-blue-900">
                      Jobberfaring: {previewData.summary.our_career_total} totalt — {previewData.summary.our_career_already_pushed} allerede pushet
                    </div>
                    {previewData.our_data.career_history.length > 0 && (
                      <ul className="text-xs text-gray-700 mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                        {previewData.our_data.career_history.map((w: any) => (
                          <li key={w.id} className="flex items-center gap-1">
                            {w.already_pushed ? <span className="text-gray-400">✓</span> : <span className="text-kf-blue-600">+</span>}
                            <span className={w.already_pushed ? 'text-gray-500' : ''}>
                              {w.occupation_title}{w.employer_name ? ` @ ${w.employer_name}` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </section>

              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
                <strong>OBS:</strong> Synchrocity bruker <code>external_id</code> for idempotens — duplikater blir
                lagt i <code>skipped[]</code> hos dem og ikke registrert dobbelt. Du kan trygt trykke "Send" uansett.
              </div>

              <div className="flex gap-2 pt-2 border-t">
                <button
                  onClick={handleConfirmPush}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Send til Karrierepasset
                </button>
                <button
                  onClick={() => { setShowPushPreview(false); setPreviewData(null); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >Avbryt</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =================== DISCONNECT MODAL =================== */}
      {showDisconnectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6 border-b bg-amber-50">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Unlink className="w-5 h-5 text-amber-600" />
                Koble fra Karrierepasset
              </h3>
            </div>

            <div className="p-6 space-y-4 text-sm">
              <p className="text-gray-700">
                Du er i ferd med å koble fra Karrierepasset. Velg hva som skal skje med data:
              </p>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteCachedData}
                  onChange={e => setDeleteCachedData(e.target.checked)}
                  className="mt-0.5"
                />
                <div>
                  <div className="font-medium text-gray-900">Slett personlighet og persona fra min profil</div>
                  <div className="text-xs text-gray-500">
                    MBTI, Holland-kode, persona og beskrivelse fjernes fra CV-bygger og innstillinger. Standard: PÅ.
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteCareerImports}
                  onChange={e => setDeleteCareerImports(e.target.checked)}
                  className="mt-0.5"
                />
                <div>
                  <div className="font-medium text-gray-900">Slett jobberfaring som ble importert fra Karrierepasset</div>
                  <div className="text-xs text-gray-500">
                    Kun rader som ble importert FRA Karrierepasset. Egen-registrert jobberfaring er ikke berørt.
                  </div>
                </div>
              </label>

              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
                <strong>Husk:</strong> Data som er sendt fra oss til Karrierepasset (attester, jobberfaring) må du selv
                slette ved å logge inn på{' '}
                <a href="https://karrierepasset.no" target="_blank" rel="noreferrer" className="underline">karrierepasset.no</a>{' '}
                og slette der eller hele profilen din.
              </div>

              <div className="flex gap-2 pt-2 border-t">
                <button
                  onClick={handleDisconnect}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                  Koble fra
                </button>
                <button
                  onClick={() => setShowDisconnectModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >Avbryt</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
